import { useState } from "react";
import { Alert, Button, InputText } from "@/deps/ui-library";
import { useLockReconciliation } from "@/api/queries";
import type { BankReconciliation, Id } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

const paisa = (value: string) => Math.round(Number(value) * 100);

/**
 * The last check before an account is locked to a date.
 *
 * Movements in our books the bank hasn't shown yet, such as an uncleared cheque, are allowed, and they're
 * part of the sum. Anything else left over stops it. Without a balance in what's been imported, the
 * closing balance is typed, because there's otherwise nothing to agree to (ADR-0050).
 */
export function FinishReconciliationDialog({
  accountId,
  asAt,
  reconciliation,
  onClose,
}: {
  accountId: Id;
  asAt: string;
  reconciliation: BankReconciliation;
  onClose: () => void;
}) {
  const lock = useLockReconciliation();
  const [typed, setTyped] = useState("");

  const cleaned = typed.replaceAll(",", "").trim();
  const readable = /^-?\d+(\.\d{1,2})?$/.test(cleaned);
  const stated = reconciliation.statementBalance ?? (readable ? cleaned : null);
  const difference =
    stated === null
      ? null
      : paisa(reconciliation.ledgerBalance) -
        (paisa(stated) + paisa(reconciliation.unmatchedInLedger));

  const rows: Array<[string, string]> = [
    ...(stated === null
      ? []
      : ([[`Statement balance at ${formatDate(asAt)}`, formatAmount(stated)]] as Array<
          [string, string]
        >)),
    ["Book balance", formatAmount(reconciliation.ledgerBalance)],
    ["In our books, not yet on the statement", formatAmount(reconciliation.unmatchedInLedger)],
    ["Difference", difference === null ? "Unknown" : formatAmount((difference / 100).toFixed(2))],
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title="Finish reconciliation"
      description={`Locks this account's statement lines up to ${formatDate(asAt)}.`}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={difference !== 0 || lock.isPending}
          onClick={() =>
            lock.mutate(
              {
                accountId,
                asAt,
                statementBalance: reconciliation.statementBalance === null ? cleaned : null,
              },
              { onSuccess: onClose },
            )
          }
        >
          {lock.isPending ? "Locking" : "Lock reconciliation"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {lock.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(lock.error as Error).message}
          />
        ) : null}
        {reconciliation.statementBalance === null ? (
          <InputText
            label="Statement closing balance"
            required
            inputMode="decimal"
            className="num"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        ) : null}
        <dl className="flex flex-col gap-1 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="border-line flex justify-between gap-3 border-t pt-1">
              <dt className="text-fg-muted">{label}</dt>
              <dd className="num text-fg">{value}</dd>
            </div>
          ))}
        </dl>
        {difference !== null && difference !== 0 ? (
          <p className="text-warn-text text-xs">
            This can&apos;t be locked until the difference is nil. Match or create what&apos;s
            missing, or check the closing balance.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
